// A useState that persists to localStorage under an app-private key, so a UI
// preference (hidden agents/columns, …) survives a refresh. Per browser profile —
// i.e. per user on their own machine — not synced across devices. Keys use the
// `recruiter_admin_app_*` prefix like the rest of this app's local state.
import { useCallback, useState, type Dispatch, type SetStateAction } from "react";

export function usePersistedState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (value) => {
      setState((prev) => {
        const next = typeof value === "function" ? (value as (p: T) => T)(prev) : value;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // Storage full / disabled — degrade to in-memory only, never throw.
        }
        return next;
      });
    },
    [key],
  );

  return [state, set];
}
